import { usd } from "../../lib/format";
import type { TypePeriodRow } from "../../types";
import { TypeIcon, type useIssuesMeta } from "../issues/meta";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { shortWeek } from "./metrics";

/** Counts and agent cost per issue type over the range picked in the page header. */
export function TypeCostTable({
  rows,
  meta,
  weeks,
  periodStart,
}: {
  rows: TypePeriodRow[];
  meta: ReturnType<typeof useIssuesMeta>["data"];
  weeks: number;
  periodStart: string | undefined;
}) {
  if (!periodStart) return null;
  const sorted = [...rows].sort(
    (a, b) => b.costUsd - a.costUsd || b.created + b.completed - (a.created + a.completed),
  );
  const total = rows.reduce(
    (sum, row) => ({
      created: sum.created + row.created,
      completed: sum.completed + row.completed,
      storyPoints: sum.storyPoints + row.storyPoints,
      runs: sum.runs + row.runs,
      costUsd: sum.costUsd + row.costUsd,
    }),
    { created: 0, completed: 0, storyPoints: 0, runs: 0, costUsd: 0 },
  );
  return (
    <Card className="gap-0 pb-0">
      <CardHeader className="border-b pb-4">
        <CardTitle>Last {weeks} weeks by type</CardTitle>
        <CardDescription>
          Since week starting {shortWeek(periodStart)} · cost of agent runs started in the period
        </CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        {sorted.length ? (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/40 text-left text-xs text-muted-foreground">
                <th className="px-4 py-2 font-medium">Type</th>
                <th className="px-2 py-2 text-right font-medium">Created</th>
                <th className="px-2 py-2 text-right font-medium">Completed</th>
                <th className="px-2 py-2 text-right font-medium">Points</th>
                <th className="px-2 py-2 text-right font-medium">Runs</th>
                <th className="px-4 py-2 text-right font-medium">Cost</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((row) => {
                const type = meta?.types.find((t) => t.key === row.typeKey);
                return (
                  <tr key={row.typeKey} className="border-b">
                    <td className="flex items-center gap-2 px-4 py-2">
                      <TypeIcon typeKey={row.typeKey} color={type?.color} />
                      {type?.name ?? row.typeKey}
                    </td>
                    <td className="px-2 py-2 text-right tabular-nums">{row.created}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{row.completed}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{row.storyPoints}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{row.runs}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{usd(row.costUsd)}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="bg-muted/40 font-medium">
                <td className="px-4 py-2">Total</td>
                <td className="px-2 py-2 text-right tabular-nums">{total.created}</td>
                <td className="px-2 py-2 text-right tabular-nums">{total.completed}</td>
                <td className="px-2 py-2 text-right tabular-nums">{total.storyPoints}</td>
                <td className="px-2 py-2 text-right tabular-nums">{total.runs}</td>
                <td className="px-4 py-2 text-right tabular-nums">{usd(total.costUsd)}</td>
              </tr>
            </tfoot>
          </table>
        ) : (
          <p className="px-4 py-6 text-center text-sm text-muted-foreground">
            No issues created, completed or run in this period.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
